import { For, Show, createMemo, type Component } from 'solid-js';
import type { PocketId, SpreadAnalysis } from '~/domain/types';
import { formatCents } from '~/domain/profiles';

/**
 * Coverage and ring-gap statistics.
 *
 * The gap figures are the wheel-side diagnostic the whole app exists for: a
 * spread can look broad on the felt and still leave a third of the ring bare.
 */

export interface CoveragePanelProps {
  analysis: SpreadAnalysis;
  currency: string;
  selectedPocket?: PocketId | null;
  onSelectPocket?: (pocketId: PocketId | null) => void;
}

const CoveragePanel: Component<CoveragePanelProps> = (props) => {
  const gaps = createMemo(() =>
    [...props.analysis.ringGaps].sort((a, b) => b.length - a.length).slice(0, 4),
  );

  const worstNet = () =>
    props.analysis.worstPocket
      ? props.analysis.netPerPocket.get(props.analysis.worstPocket) ?? 0
      : 0;
  const bestNet = () =>
    props.analysis.bestPocket
      ? props.analysis.netPerPocket.get(props.analysis.bestPocket) ?? 0
      : 0;

  return (
    <section class="rounded-lg border border-line bg-surface-1 p-3">
      <h2 class="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
        Coverage
      </h2>

      <dl class="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
        <Stat
          label="Stake"
          value={`${props.analysis.totalStake} chips`}
          sub={formatCents(props.analysis.totalStakeCents, props.currency)}
        />
        <Stat
          label="Pockets covered"
          value={`${props.analysis.pocketsCovered}/${props.analysis.wheel.pocketCount}`}
          sub={`${props.analysis.coveragePct.toFixed(1)}% of the ring`}
        />
        <Stat
          label="Best pocket"
          value={props.analysis.bestPocket ?? '—'}
          sub={props.analysis.bestPocket ? `+${formatCents(bestNet(), props.currency)}` : ''}
          tone="gain"
        />
        <Stat
          label="Worst pocket"
          value={props.analysis.worstPocket ?? '—'}
          sub={props.analysis.worstPocket ? formatCents(worstNet(), props.currency) : ''}
          tone="loss"
        />
      </dl>

      <div class="mt-3 border-t border-line pt-2">
        <div class="flex items-baseline justify-between">
          <h3 class="text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
            Bare runs on the ring
          </h3>
          <span class="tnum text-[11px] text-ink">
            longest {props.analysis.longestGap?.length ?? 0}
          </span>
        </div>

        <Show
          when={gaps().length > 0}
          fallback={
            <p class="mt-1 text-[11px] text-gain">
              Every pocket on the wheel is covered.
            </p>
          }
        >
          <ul class="mt-1.5 space-y-1">
            <For each={gaps()}>
              {(gap) => (
                <li class="flex items-center gap-2 text-[11px]">
                  <span class="tnum w-6 shrink-0 text-ink">{gap.length}</span>
                  <span class="truncate text-[10px] text-ink-faint" title={gap.pockets.join(' ')}>
                    {gap.pockets.join(' ')}
                  </span>
                </li>
              )}
            </For>
          </ul>
          <p class="mt-1.5 text-[10px] leading-relaxed text-ink-faint">
            Runs are consecutive pockets on the physical wheel, not the felt — a dozen is
            twelve adjacent cells on the layout and twelve scattered pockets on the ring.
          </p>
        </Show>
      </div>

      <Show when={props.selectedPocket}>
        {(pocketId) => (
          <div class="mt-3 flex items-baseline justify-between border-t border-line pt-2 text-[11px]">
            <span class="text-ink-dim">
              Pocket <span class="tnum text-ink">{pocketId()}</span> —{' '}
              <span class="tnum">{props.analysis.coverage.get(pocketId())?.chips ?? 0}</span>{' '}
              chips across{' '}
              <span class="tnum">{props.analysis.coverage.get(pocketId())?.bets.length ?? 0}</span>{' '}
              bets
            </span>
            <span
              class="tnum"
              classList={{
                'text-gain': (props.analysis.netPerPocket.get(pocketId()) ?? 0) >= 0,
                'text-loss': (props.analysis.netPerPocket.get(pocketId()) ?? 0) < 0,
              }}
            >
              {formatCents(props.analysis.netPerPocket.get(pocketId()) ?? 0, props.currency)}
            </span>
          </div>
        )}
      </Show>
    </section>
  );
};

const Stat: Component<{
  label: string;
  value: string;
  sub?: string;
  tone?: 'gain' | 'loss';
}> = (props) => (
  <div class="flex flex-col">
    <dt class="text-ink-faint">{props.label}</dt>
    <dd class="tnum text-ink">{props.value}</dd>
    <Show when={props.sub}>
      <dd
        class="tnum text-[10px]"
        classList={{
          'text-gain': props.tone === 'gain',
          'text-loss': props.tone === 'loss',
          'text-ink-faint': props.tone === undefined,
        }}
      >
        {props.sub}
      </dd>
    </Show>
  </div>
);

export default CoveragePanel;
